import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "avatar", "uploadButton", "modal", "fileInput", "preview", 
    "canvas", "cropArea", "saveButton", "cancelButton",
    "zoomSlider", "rotateButton"
  ]
  
  static values = {
    userId: String,
    updateUrl: String
  }
  
  connect() {
    this.imageData = null
    this.cropData = {
      x: 0,
      y: 0,
      width: 200,
      height: 200,
      scale: 1,
      rotation: 0
    }
    this.isDragging = false
    this.isPinching = false
    this.dragStart = { x: 0, y: 0 }
    this.pinchStartDistance = 0
    this.pinchStartScale = 1
    this.setupEventListeners()
  }
  
  disconnect() {
    this.removeEventListeners()
  }
  
  setupEventListeners() {
    if (this.hasCanvasTarget) {
      this.canvas = this.canvasTarget
      this.ctx = this.canvas.getContext('2d')
      
      // Set cursor style
      this.canvas.style.cursor = 'grab'
      
      this.canvas.addEventListener('mousedown', this.startDrag.bind(this))
      this.canvas.addEventListener('mousemove', this.drag.bind(this))
      this.canvas.addEventListener('mouseup', this.endDrag.bind(this))
      this.canvas.addEventListener('mouseleave', this.endDrag.bind(this))
      this.canvas.addEventListener('wheel', this.handleZoom.bind(this))

      // Mobile touch drag support
      this.canvas.addEventListener('touchstart', this.startTouchDrag.bind(this), { passive: false })
      this.canvas.addEventListener('touchmove', this.touchDrag.bind(this), { passive: false })
      this.canvas.addEventListener('touchend', this.endDrag.bind(this), { passive: false })
      this.canvas.addEventListener('touchcancel', this.endDrag.bind(this), { passive: false })
    }
  }
  
  removeEventListeners() {
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.startDrag.bind(this))
      this.canvas.removeEventListener('mousemove', this.drag.bind(this))
      this.canvas.removeEventListener('mouseup', this.endDrag.bind(this))
      this.canvas.removeEventListener('wheel', this.handleZoom.bind(this))
    }
  }
  
  openEditor() {
    this.modalTarget.classList.remove('hidden')
    this.modalTarget.classList.add('flex')
    document.body.classList.add('overflow-hidden', 'avatar-editor-open')
  }
  
  closeEditor() {
    this.modalTarget.classList.add('hidden')
    this.modalTarget.classList.remove('flex')
    document.body.classList.remove('overflow-hidden', 'avatar-editor-open')
    this.resetEditor()
  }
  
  resetEditor() {
    this.imageData = null
    this.cropData = {
      x: 0,
      y: 0,
      width: 200,
      height: 200,
      scale: 1,
      rotation: 0
    }
    this.fileInputTarget.value = ''
    if (this.hasZoomSliderTarget) {
      this.zoomSliderTarget.value = 1
    }
  }
  
  handleFileSelect() {
    const file = this.fileInputTarget.files[0]
    if (!file) return
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.')
      return
    }
    
    const reader = new FileReader()
    reader.onload = (e) => {
      this.loadImage(e.target.result)
    }
    reader.readAsDataURL(file)
  }
  
  loadImage(src) {
    const img = new Image()
    img.onload = () => {
      this.imageData = {
        img: img,
        originalWidth: img.width,
        originalHeight: img.height
      }
      this.setupCanvas()
      this.drawImage()
    }
    img.src = src
  }
  
  setupCanvas() {
    // Use higher resolution for better quality
    this.canvas.width = 600
    this.canvas.height = 600
    
    // Center the crop area - make it larger and more intuitive
    const size = Math.min(this.canvas.width, this.canvas.height) * 0.8
    this.cropData.width = size
    this.cropData.height = size
    this.cropData.x = (this.canvas.width - size) / 2
    this.cropData.y = (this.canvas.height - size) / 2
    
    // Initialize image position and scale
    this.imagePosition = { x: 0, y: 0 }
    this.imageScale = 1
  }
  
  drawImage() {
    if (!this.imageData) return
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    
    // Calculate image dimensions to fit canvas while maintaining aspect ratio
    const { img } = this.imageData
    const canvasSize = Math.min(this.canvas.width, this.canvas.height)
    const imageAspect = img.width / img.height
    
    let baseWidth, baseHeight
    
    // Fit image to canvas, covering the entire area
    if (imageAspect > 1) {
      baseHeight = canvasSize
      baseWidth = baseHeight * imageAspect
    } else {
      baseWidth = canvasSize
      baseHeight = baseWidth / imageAspect
    }
    
    // Apply user scaling
    const drawWidth = baseWidth * this.imageScale
    const drawHeight = baseHeight * this.imageScale
    
    // Apply user positioning (centered by default)
    const drawX = (this.canvas.width - drawWidth) / 2 + this.imagePosition.x
    const drawY = (this.canvas.height - drawHeight) / 2 + this.imagePosition.y
    
    // Save context for rotation and high-quality rendering
    this.ctx.save()
    this.ctx.imageSmoothingEnabled = true
    this.ctx.imageSmoothingQuality = 'high'
    
    // Apply rotation around center
    if (this.cropData.rotation !== 0) {
      this.ctx.translate(this.canvas.width / 2, this.canvas.height / 2)
      this.ctx.rotate((this.cropData.rotation * Math.PI) / 180)
      this.ctx.translate(-this.canvas.width / 2, -this.canvas.height / 2)
    }
    
    // Draw the image with high quality
    this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
    
    // Restore context
    this.ctx.restore()
    
    // Draw crop overlay
    this.drawCropOverlay()
  }
  
  drawCropOverlay() {
    this.ctx.save()
    
    // Draw subtle overlay outside crop area
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    
    // Clear the crop area (make it transparent)
    this.ctx.globalCompositeOperation = 'destination-out'
    this.ctx.beginPath()
    this.ctx.arc(
      this.canvas.width / 2,
      this.canvas.height / 2,
      this.cropData.width / 2,
      0,
      2 * Math.PI
    )
    this.ctx.fill()
    
    // Draw clean crop circle border
    this.ctx.globalCompositeOperation = 'source-over'
    this.ctx.strokeStyle = '#FFFFFF'
    this.ctx.lineWidth = 3
    this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
    this.ctx.shadowBlur = 6
    this.ctx.beginPath()
    this.ctx.arc(
      this.canvas.width / 2,
      this.canvas.height / 2,
      this.cropData.width / 2,
      0,
      2 * Math.PI
    )
    this.ctx.stroke()
    
    // Add inner border for clarity
    this.ctx.shadowBlur = 0
    this.ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)'
    this.ctx.lineWidth = 1
    this.ctx.beginPath()
    this.ctx.arc(
      this.canvas.width / 2,
      this.canvas.height / 2,
      this.cropData.width / 2 - 2,
      0,
      2 * Math.PI
    )
    this.ctx.stroke()
    
    this.ctx.restore()
  }
  
  startDrag(event) {
    const rect = this.canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    this.isDragging = true
    this.dragStart = { x: x - this.imagePosition.x, y: y - this.imagePosition.y }
    this.canvas.style.cursor = 'grabbing'
    event.preventDefault()
  }

  startTouchDrag(event) {
    if (!event.touches || event.touches.length === 0) return

    if (event.touches.length >= 2) {
      // Pinch/rotate start
      this.isDragging = false
      this.isPinching = true
      this.pinchStartDistance = this.getTouchDistance(event.touches[0], event.touches[1])
      this.pinchStartScale = this.imageScale || 1
      this.pinchStartAngle = this.getTouchAngle(event.touches[0], event.touches[1])
      this.pinchStartRotation = this.cropData.rotation || 0
      event.preventDefault()
      return
    }

    this.isPinching = false
    const touch = event.touches[0]
    this.startDrag({
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => event.preventDefault()
    })
  }
  
  drag(event) {
    if (!this.isDragging) return

    const rect = this.canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    this.imagePosition.x = x - this.dragStart.x
    this.imagePosition.y = y - this.dragStart.y

    this.drawImage()
    event.preventDefault()
  }

  touchDrag(event) {
    if (!event.touches || event.touches.length === 0) return

    if (event.touches.length >= 2) {
      // Pinch zoom + rotate twist
      const distance = this.getTouchDistance(event.touches[0], event.touches[1])
      const angle = this.getTouchAngle(event.touches[0], event.touches[1])

      if (this.pinchStartDistance > 0) {
        const scaleFactor = distance / this.pinchStartDistance
        this.imageScale = Math.max(0.5, Math.min(3, this.pinchStartScale * scaleFactor))

        if (this.hasZoomSliderTarget) {
          this.zoomSliderTarget.value = this.imageScale
        }
      }

      if (typeof this.pinchStartAngle === 'number') {
        let delta = angle - this.pinchStartAngle
        if (delta > 180) delta -= 360
        if (delta < -180) delta += 360
        this.cropData.rotation = this.pinchStartRotation + delta
      }

      this.drawImage()
      event.preventDefault()
      return
    }

    // Single finger drag
    if (this.isPinching) {
      this.isPinching = false
      this.startTouchDrag(event)
      return
    }

    const touch = event.touches[0]
    this.drag({
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => event.preventDefault()
    })
  }

  endDrag() {
    this.isDragging = false
    this.isPinching = false
    this.pinchStartDistance = 0
    this.pinchStartAngle = null
    this.canvas.style.cursor = 'grab'
  }

  getTouchDistance(t1, t2) {
    const dx = t2.clientX - t1.clientX
    const dy = t2.clientY - t1.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  getTouchAngle(t1, t2) {
    const dx = t2.clientX - t1.clientX
    const dy = t2.clientY - t1.clientY
    return Math.atan2(dy, dx) * (180 / Math.PI)
  }
  
  handleZoom(event) {
    event.preventDefault()
    const delta = event.deltaY > 0 ? -0.1 : 0.1
    this.imageScale = Math.max(0.5, Math.min(3, this.imageScale + delta))
    
    if (this.hasZoomSliderTarget) {
      this.zoomSliderTarget.value = this.imageScale
    }
    
    this.drawImage()
  }
  
  updateZoom() {
    if (this.hasZoomSliderTarget) {
      this.imageScale = parseFloat(this.zoomSliderTarget.value)
      this.drawImage()
    }
  }
  
  rotateImage() {
    this.cropData.rotation = (this.cropData.rotation + 90) % 360
    this.drawImage()
  }
  
  async saveAvatar() {
    if (!this.imageData) return
    
    try {
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Saving...'
      
      // Build clean export image WITHOUT overlay/circle mask
      const sourceCanvas = document.createElement('canvas')
      sourceCanvas.width = this.canvas.width
      sourceCanvas.height = this.canvas.height
      const sourceCtx = sourceCanvas.getContext('2d')
      sourceCtx.imageSmoothingEnabled = true
      sourceCtx.imageSmoothingQuality = 'high'

      // Re-draw only the transformed original image (no dark overlay, no guide circle)
      const { img } = this.imageData
      const canvasSize = Math.min(this.canvas.width, this.canvas.height)
      const imageAspect = img.width / img.height

      let baseWidth, baseHeight
      if (imageAspect > 1) {
        baseHeight = canvasSize
        baseWidth = baseHeight * imageAspect
      } else {
        baseWidth = canvasSize
        baseHeight = baseWidth / imageAspect
      }

      const drawWidth = baseWidth * this.imageScale
      const drawHeight = baseHeight * this.imageScale
      const drawX = (this.canvas.width - drawWidth) / 2 + this.imagePosition.x
      const drawY = (this.canvas.height - drawHeight) / 2 + this.imagePosition.y

      sourceCtx.save()
      if (this.cropData.rotation !== 0) {
        sourceCtx.translate(this.canvas.width / 2, this.canvas.height / 2)
        sourceCtx.rotate((this.cropData.rotation * Math.PI) / 180)
        sourceCtx.translate(-this.canvas.width / 2, -this.canvas.height / 2)
      }
      sourceCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
      sourceCtx.restore()

      // Export square crop (avatar circle is handled by CSS on display)
      const croppedCanvas = document.createElement('canvas')
      const outputSize = 600
      croppedCanvas.width = outputSize
      croppedCanvas.height = outputSize
      const croppedCtx = croppedCanvas.getContext('2d')
      croppedCtx.imageSmoothingEnabled = true
      croppedCtx.imageSmoothingQuality = 'high'

      const cropCenterX = this.canvas.width / 2
      const cropCenterY = this.canvas.height / 2
      const cropRadius = this.cropData.width / 2
      const sourceX = cropCenterX - cropRadius
      const sourceY = cropCenterY - cropRadius
      const sourceSize = cropRadius * 2

      croppedCtx.drawImage(
        sourceCanvas,
        sourceX, sourceY, sourceSize, sourceSize,
        0, 0, outputSize, outputSize
      )
      
      // Convert to PNG blob to preserve transparency
      const blob = await new Promise(resolve => {
        croppedCanvas.toBlob(resolve, 'image/png')
      })
      
      // Upload via FormData
      const formData = new FormData()
      formData.append('user[avatar]', blob, 'avatar.png')
      formData.append('authenticity_token', this.getCSRFToken())
      
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // Update avatar everywhere in current page/session
          if (data.avatar_url) {
            const cacheBustedUrl = `${data.avatar_url}?t=${Date.now()}`

            // Profile avatar in editor card
            let avatarImg = this.avatarTarget.querySelector('img') || this.avatarTarget
            if (avatarImg.tagName === 'IMG') {
              avatarImg.src = cacheBustedUrl
            } else {
              const newImg = document.createElement('img')
              newImg.src = cacheBustedUrl
              newImg.className = 'w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover'
              newImg.setAttribute('data-avatar-editor-target', 'avatar')
              newImg.setAttribute('data-avatar-user-id', this.userIdValue)
              this.avatarTarget.replaceWith(newImg)
              this.avatarTarget = newImg
            }

            // Any avatar image marked with this user id (dock, posts, profile bits)
            document.querySelectorAll(`img[data-avatar-user-id="${this.userIdValue}"]`).forEach((img) => {
              img.src = cacheBustedUrl
            })

            // Common current-user avatar hooks (composer/header)
            document.querySelectorAll('[data-posting-as-target="userAvatar"], .app-header-avatar, .app-nav-dropdown-avatar, .dock-avatar-img').forEach((img) => {
              if (img.tagName === 'IMG') img.src = cacheBustedUrl
            })
          }
          
          this.closeEditor()
          this.showNotification('Avatar updated successfully!', 'success')
        } else {
          throw new Error(data.error || 'Failed to update avatar')
        }
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
      
    } catch (error) {
      console.error('Avatar update error:', error)
      this.showNotification('Failed to update avatar. Please try again.', 'error')
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.innerHTML = '<i class="fas fa-save mr-2"></i>Save Avatar'
    }
  }
  
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }
  
  showNotification(message, type = 'info') {
    // Simple notification - you can enhance this with a proper notification system
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-white ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    }`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}